<template>
  <div class="admin-dashboard">
    <main>
      <div class="card">
  <!-- Gunakan container flexbox untuk header dan form -->
  <div class="add-form-container">
    <h2>Manajemen Paket Tes</h2>
    
    <form @submit.prevent="addPackage" class="add-form">
      <div class="form-group">
        <label for="new-pkg-name">Nama Paket</label>
        <input id="new-pkg-name" v-model="newPackage.name" placeholder="Nama Paket" required />
      </div>

      <div class="form-group">
        <label for="new-pkg-code">Kode Akses</label>
        <input id="new-pkg-code" v-model="newPackage.access_code" placeholder="Kode Akses" required />
      </div>

      <div class="form-group">
        <label for="new-pkg-company">Perusahaan</label>
        <input id="new-pkg-company" v-model="newPackage.company_name" placeholder="Nama Perusahaan (Default)" />
      </div>

      <!-- Tampilkan dropdown Gerai HANYA untuk Psikolog/Superadmin -->
      <div v-if="authStore.user && !authStore.user.gerai_id" class="form-group">
        <label for="new-pkg-gerai">Gerai</label>
        <select id="new-pkg-gerai" v-model="newPackage.gerai_id">
          <option :value="null">-- Tidak Terikat Gerai --</option>
          <option v-for="gerai in allGerai" :key="gerai.id" :value="gerai.id">
            {{ gerai.name }}
          </option>
        </select>
      </div>

      <div class="checkbox-group">
        <input id="new-pkg-active" v-model="newPackage.is_active" type="checkbox" />
        <label for="new-pkg-active">Aktif</label>
      </div>

      <button type="submit" :disabled="isSubmitting">Tambah Paket</button>
    </form>
  </div>

  <hr />

        <h3>Daftar Paket Tes</h3>
        <div v-if="isLoading">Memuat data...</div>
        <div class="table-container" v-else-if="packages.length > 0">
          <table>
            <thead>
              <tr>
                <th>Nama Paket</th>
                <th>Kode Akses</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in packages" :key="pkg.id">
                <td>
                  <router-link :to="{ name: 'PackageDetail', params: { id: pkg.id } }">
                    {{ pkg.name }}
                  </router-link>
                </td>
                <td><code>{{ pkg.access_code }}</code></td>
                <td>{{ pkg.is_active ? 'Aktif' : 'Tidak Aktif' }}</td>
                <!-- TOMBOL UNTUK MEMANGGIL 'tryTest' -->
                <td>
                  <button @click="tryTest(pkg.access_code)" class="try-button">Coba Tes</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else>Belum ada paket tes yang dibuat.</p>
        <p v-if="error" class="error">{{ error }}</p>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth'; // Import Pinia auth store
import apiClient from '@/services/api.js';

const router = useRouter();
const authStore = useAuthStore(); // Gunakan store

// State untuk daftar paket
const packages = ref([]);
const isLoading = ref(true);
const error = ref('');

// State untuk form tambah paket baru
const isSubmitting = ref(false);
const newPackage = ref({
  name: '',
  access_code: '',
  is_active: true,
  gerai_id: null, // <-- TAMBAHKAN gerai_id
});

// State untuk daftar gerai
const allGerai = ref([]);

// Fungsi untuk mengambil daftar paket
const fetchPackages = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const response = await apiClient.get('/admin/packages/'); 
    packages.value = response.data.packages;
  } catch (err) {
    error.value = 'Gagal memuat data paket tes.';
    if (err.response && err.response.status === 401) {
      authStore.logout();
      router.push({ name: 'AdminLogin' });
    }
  } finally {
    isLoading.value = false;
  }
};

// Fungsi untuk mengambil daftar gerai
const fetchAllGerai = async () => {
  // Hanya ambil jika user adalah psikolog/superadmin
  if (authStore.user && !authStore.user.gerai_id) {
    try {
      const response = await apiClient.get('/admin/gerai/');
      allGerai.value = response.data;
    } catch (err) {
      console.error("Gagal memuat daftar gerai.", err);
    }
  }
};

// Fungsi untuk menambah paket baru
const addPackage = async () => {
  isSubmitting.value = true;
  error.value = '';
  try {
    // Payload sudah otomatis berisi gerai_id
    await apiClient.post('/admin/packages/', newPackage.value);
    // Reset form
    newPackage.value = { name: '', access_code: '', is_active: true, gerai_id: null };
    await fetchPackages(); // Muat ulang daftar paket
  } catch (err) {
    error.value = 'Gagal menambah paket tes. Kode akses mungkin sudah ada.';
    console.error(err);
  } finally {
    isSubmitting.value = false;
  }
};

// Fungsi untuk membuka tab baru dan mencoba tes
const tryTest = (accessCode) => {
  localStorage.setItem('preview_access_code', accessCode);
  const routeData = router.resolve({ name: 'Home' });
  window.open(routeData.href, '_blank');
};

// Saat komponen dimuat, ambil semua data yang diperlukan
onMounted(async () => {
  // Pastikan data user sudah ada di store sebelum melanjutkan
  await authStore.fetchUser();
  
  // Ambil data paket dan gerai
  fetchPackages();
  fetchAllGerai();
});
</script>

<style scoped>
.admin-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #333;
}

/* === CARD UTAMA === */
.card {
  background: #fff;
  padding: 28px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  border: 1px solid #eee;
  margin-bottom: 28px;
}

/* === HEADER DAN JUDUL === */
h2 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 1.4em;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 15px;
}
h3 {
  margin: 0 0 15px 0;
  font-size: 1.1em;
  color: #333;
}

/* === FORM TAMBAH PAKET BARU === */
.add-form-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}

.add-form-container h2 {
  border-bottom: none;
  padding-bottom: 0;
}

.add-form {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: flex-end; /* Penting untuk mensejajarkan tombol */
}

/* Penataan input form */
.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 0.9em;
  margin-bottom: 5px;
  color: #555;
}

.form-group input,
.form-group select {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1em;
  min-width: 200px; /* Lebar minimum untuk input */
}

/* Penataan checkbox */
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px; /* Samakan tinggi dengan tombol */
  padding-bottom: 2px;
}
.checkbox-group input {
  width: 16px;
  height: 16px;
}

/* Tombol Tambah */
.add-form button {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background-color: #1abc9c;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  height: 40px; /* Samakan tinggi dengan input */
}
.add-form button:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
}
.add-form button:hover:not(:disabled) {
  background-color: #16a085;
}

/* === TABEL DAFTAR PAKET === */
.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}

th, td {
  text-align: left;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
}

th {
  background-color: #f8f9fa;
  color: #555;
  font-weight: 600;
  font-size: 0.9em;
  text-transform: uppercase;
}

tr:last-child td {
  border-bottom: none;
}

tr:hover {
  background-color: #f9fafb;
}

/* Nama paket yang bisa diklik */
td a {
  color: #3498db;
  text-decoration: none;
  font-weight: 500;
}
td a:hover {
  text-decoration: underline;
}

/* Tombol Aksi */
.try-button {
  font-size: 0.85em;
  padding: 6px 12px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}
.try-button:hover {
  background-color: #2980b9;
}

/* === STATE LAINNYA === */
.loading, .error {
  text-align: center;
  padding: 20px;
  color: #777;
}
.error {
  color: #e74c3c;
}
</style>